export interface BandableOverride {
  id: number
  date: string
  /**
   * WHICH HOTEL THE OVERRIDE BELONGS TO (Groups). Part of a row's identity, not decoration: at a
   * lead the list spans hotels, `roomType` is a NAME, and two hotels legitimately hold a room called
   * "Double room-Standard". Without this in the signature, that room's override on the 1st at one
   * hotel and on the 2nd at another would band into a single two-day run — one line hiding two
   * hotels, and a delete that reached into both. Undefined everywhere ⇒ identical banding to before.
   */
  propertyId?: number | null
  propertyName?: string | null
  roomType: string
  ratePlan: string
  overrideType: string
  mode?: string
  adjustValue?: number | null
  releaseOn?: string | null
  rateAmount: number | null
  stopSell: boolean | null
  closedToArrival: boolean | null
  closedToDeparture: boolean | null
  minStayArrival: number | null
  minStayThrough: number | null
  maxStay: number | null
  reason: string
}

export interface OverrideBand<T extends BandableOverride> {
  key: string
  startDate: string
  endDate: string
  days: number
  /** Every row in the band, ascending by date. A one-day band has exactly one. */
  members: T[]
  /** The first row — every member is identical apart from its date. */
  head: T
}

const DAY_MS = 86_400_000

/**
 * Collapse per-day override rows into contiguous runs for display.
 *
 * A bulk action writes one row per (day × room) — that is the storage model, and
 * deliberately so: one row per day is the only thing the engine, the change log
 * and the Eviivo push all agree on, and it is what lets a single day inside a
 * span be edited without any split/merge bookkeeping.
 *
 * The cost is a wall of near-identical rows, and the fix for that is presentation,
 * not a stored grouping entity. These bands are DERIVED on every render from the
 * rows themselves, so they cannot go stale, cannot disagree with their members,
 * and cannot be orphaned. Change one day inside a run and the band simply splits
 * on the next render, because the values genuinely differ.
 *
 * Rows band together only when everything except the date matches.
 */
export function bandOverrides<T extends BandableOverride>(overrides: T[]): OverrideBand<T>[] {
  const signature = (o: T) =>
    JSON.stringify([
      o.propertyId ?? null,
      o.roomType, o.ratePlan, o.overrideType, o.mode ?? 'fixed', o.adjustValue ?? null,
      o.releaseOn ?? null, o.rateAmount, o.stopSell, o.closedToArrival, o.closedToDeparture,
      o.minStayArrival, o.minStayThrough, o.maxStay, o.reason,
    ])

  // Signed once per row, not once per comparison: a comparator that stringifies
  // both sides costs O(n log n) serialisations, and a property with a season of
  // bulk overrides has thousands of rows to sort on every render.
  const signed = overrides.map((row) => ({ row, sig: signature(row) }))
  signed.sort((a, b) => a.sig.localeCompare(b.sig) || a.row.date.localeCompare(b.row.date))

  const bands: OverrideBand<T>[] = []
  let openSig: string | null = null

  for (const { row, sig } of signed) {
    const open = bands[bands.length - 1]
    const contiguous =
      open != null &&
      openSig === sig &&
      new Date(`${row.date}T00:00:00`).getTime() -
        new Date(`${open.endDate}T00:00:00`).getTime() ===
        DAY_MS

    if (contiguous) {
      open.endDate = row.date
      open.days += 1
      open.members.push(row)
    } else {
      openSig = sig
      bands.push({
        key: `band-${row.id}`,
        startDate: row.date,
        endDate: row.date,
        days: 1,
        members: [row],
        head: row,
      })
    }
  }

  return bands.sort((a, b) => a.startDate.localeCompare(b.startDate))
}
